// src/pages/Products/AddProductForm.jsx — إضافة صنف وتعديله (نفس الشاشة: /edit/:id تفتح التعديل)
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "../../axios";
import {
  Container,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Typography,
  Divider,
  IconButton,
  Paper,
  Chip,
  Alert,
  Fade,
  useTheme,
  useMediaQuery,
  Tooltip,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import QrCodeIcon from "@mui/icons-material/QrCode";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ScaleIcon from "@mui/icons-material/Scale";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import StoreIcon from "@mui/icons-material/Store";
import NumbersIcon from "@mui/icons-material/Numbers";
import API_BASE_URL from "../../config";

export default function AddProductForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const companyId = Number(localStorage.getItem("companyId") || 0);
  const branchIdFromLogin = Number(localStorage.getItem("branchId") || 0);

  const [formData, setFormData] = useState({
    arabicName: "",
    englishName: "",
    description: "",
    imageUrl: "",
    code: "",
    itemId: 0,
    taxId: 0,
    companyId,
    branchId: branchIdFromLogin,
    categoryId: 0,
    isService: false,
    showInPOS: true,
    units: [],
  });

  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vatTypes, setVatTypes] = useState([]);
  const [unitDefs, setUnitDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  // ── عدم التكرار: رسائل التعارض من الخادم (الاسم/الكود/باركود كل وحدة)
  const [dup, setDup] = useState({ arabicName: "", englishName: "", code: "", barcodes: {} });
  const [checking, setChecking] = useState({});
  const [saving, setSaving] = useState(false);

  // دالة لتحديد النص عند التركيز
  const handleFocus = (event) => {
    if (event && event.target) {
      event.target.select();
    }
  };

  // التحقق من عدم التكرار عند الخروج من الحقل (الخادم يستثني الصنف نفسه في التعديل)
  const checkUnique = async (field, value, unitIdx) => {
    const v = String(value ?? "").trim();
    const key = field === "barcode" ? `b${unitIdx}` : field;
    const setMsg = (msg) => setDup((d) => field === "barcode"
      ? { ...d, barcodes: { ...d.barcodes, [unitIdx]: msg } }
      : { ...d, [field]: msg });
    if (!v) { setMsg(""); return ""; }
    setChecking((c) => ({ ...c, [key]: true }));
    try {
      const { data } = await axios.get(`${API_BASE_URL}/Product/check-unique`, {
        params: { companyId, itemId: isEdit ? formData.itemId : undefined, [field]: v },
      });
      const msg = data?.[field] || "";
      setMsg(msg);
      return msg;
    } catch {
      return "";            // تعذّر التحقق الآن — الخادم يتحقق مرة أخرى عند الحفظ
    } finally {
      setChecking((c) => ({ ...c, [key]: false }));
    }
  };

  // باركود مكرر بين وحدات نفس الصنف (بدون طلب للخادم)
  const localBarcodeDup = (idx) => {
    const b = String(formData.units[idx]?.barcode ?? "").trim().toLowerCase();
    if (!b) return "";
    return formData.units.some((u, j) => j !== idx && String(u.barcode ?? "").trim().toLowerCase() === b)
      ? "❌ الباركود مكرر في وحدة أخرى لنفس الصنف"
      : "";
  };
  const barcodeError = (idx) => localBarcodeDup(idx) || dup.barcodes[idx] || "";

  // توليد كود المنتج
  const generateProductCode = async (categoryId, branchId, companyId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/Product/max-id?companyId=${companyId}`
      );
      const maxId = response.data || 0;
      const nextItemId = maxId + 1;

      setFormData((prev) => ({
        ...prev,
        itemId: nextItemId,
      }));

      return `${companyId}00${branchId}0${categoryId}000${nextItemId}`;
    } catch (error) {
      console.error("خطأ في توليد كود المنتج:", error);
      return "";
    }
  };

  // تغيير الحقول الرئيسية
  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    if (name === "arabicName" || name === "englishName" || name === "code") {
      setDup((d) => (d[name] ? { ...d, [name]: "" } : d));
    }

    if (name === "categoryId" && value && !isEdit) {
      const newCode = await generateProductCode(
        value,
        formData.branchId,
        formData.companyId
      );
      setFormData((prev) => ({
        ...prev,
        categoryId: newValue,
        code: newCode,
      }));
      checkUnique("code", newCode);
    }

    if (name === "branchId" && value && formData.categoryId && !isEdit) {
      const newCode = await generateProductCode(
        formData.categoryId,
        value,
        formData.companyId
      );
      setFormData((prev) => ({
        ...prev,
        branchId: newValue,
        code: newCode,
      }));
      checkUnique("code", newCode);
    }
  };

  // تحميل البيانات
  useEffect(() => {
    async function fetchData() {
      try {
        const [bRes, cRes, vRes, uRes, pRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/Branch/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/Category/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/VatTypes?companyId=${companyId}`),
          axios.get(`${API_BASE_URL}/Unit/by-company/${companyId}`),
          isEdit ? axios.get(`${API_BASE_URL}/Product/by-company/${companyId}`) : Promise.resolve(null),
        ]);

        setBranches(bRes.data);
        setCategories(cRes.data);
        setVatTypes(vRes.data);
        setUnitDefs(uRes.data);

        // ── وضع التعديل: تحميل الصنف ──
        if (isEdit) {
          const list = Array.isArray(pRes?.data) ? pRes.data : [];
          const product = list.find((x) => String(x.id) === String(id));
          if (!product) { setError("لم يتم العثور على الصنف المطلوب."); return; }
          const units = (product.units || []).map((u) => ({
            unitId: u.unitId,
            factor: u.isBaseUnit ? 1 : (u.factor || 1),
            salPrice: u.salprice ?? u.salPrice ?? 0,
            purchPrice: u.purchprice ?? u.purchPrice ?? 0,
            barcode: u.barcode || "",
            isBaseUnit: !!u.isBaseUnit,
          }));
          if (units.length && !units.some(u => u.isBaseUnit)) units[0].isBaseUnit = true;
          setFormData((prev) => ({
            ...prev,
            arabicName: product.arabicName || "",
            englishName: product.englishName || "",
            description: product.description || "",
            imageUrl: product.imageUrl || "", // ✅ كان يُرسل فارغًا فيمسح صورة الصنف
            code: product.code || "",
            itemId: product.itemId || 0,
            taxId: product.taxId || (vRes.data[0]?.id ?? 0),
            branchId: branchIdFromLogin || (bRes.data[0]?.id ?? 0),
            categoryId: product.categoryId || (cRes.data[0]?.id ?? 0),
            isService: !!product.isService, // ✅ يتطلب IsService في /Product/by-company
            showInPOS: product.showInPOS !== false,
            units: units.length ? units : [{ unitId: uRes.data[0]?.id ?? 0, factor: 1, salPrice: 0, purchPrice: 0, barcode: "", isBaseUnit: true }],
          }));
          return;
        }

        const defaultBranchId =
          branchIdFromLogin > 0
            ? branchIdFromLogin
            : bRes.data.length > 0
            ? bRes.data[0].id
            : 0;

        const defaultCategoryId = cRes.data.length > 0 ? cRes.data[0].id : 0;

        let defaultCode = "";
        if (defaultCategoryId && defaultBranchId) {
          defaultCode = await generateProductCode(
            defaultCategoryId,
            defaultBranchId,
            companyId
          );
        }

        setFormData((prev) => ({
          ...prev,
          branchId: defaultBranchId,
          categoryId: defaultCategoryId,
          taxId: vRes.data.length > 0 ? vRes.data[0].id : 0,
          code: defaultCode,
          units:
            prev.units.length === 0 && uRes.data.length > 0
              ? [
                  {
                    unitId: uRes.data[0].id,
                    factor: 1,
                    salPrice: 0,
                    purchPrice: 0,
                    barcode: "",
                    isBaseUnit: true,
                  },
                ]
              : prev.units,
        }));
      } catch (err) {
        console.error(err);
        setError("فشل في تحميل البيانات.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [companyId, branchIdFromLogin, id]);

  // إضافة وحدة
  const addUnit = () => {
    setFormData((prev) => {
      const used = prev.units.map(u => u.unitId);
      const free = unitDefs.find(u => !used.includes(u.id));
      const first = prev.units.length === 0;
      return {
        ...prev,
        units: [
          ...prev.units,
          {
            unitId: free ? free.id : (unitDefs[0]?.id ?? 0),
            factor: first ? 1 : "",
            salPrice: 0,
            purchPrice: 0,
            barcode: "",
            isBaseUnit: first,
          },
        ],
      };
    });
  };

  const preventWheelChange = (e) => {
  // منع عجلة الماوس من تغيير القيمة الرقمية
  e.preventDefault();
  e.stopPropagation();

  // إزالة التركيز لمنع المتصفح من تعديل الرقم
  e.currentTarget.blur();
};

  // ───────────── تعادل الوحدات ─────────────
  // الوحدة الأساسية معاملها 1، وكل وحدة أخرى = كم وحدة أساسية تحتوي (كرتون = 24 حبة)
  const toNum = (v) => { const n = parseFloat(String(v ?? "").replace(/[\u0660-\u0669]/g, d => String(d.charCodeAt(0) - 0x0660))); return Number.isFinite(n) ? n : 0; };
  const round = (v, d = 4) => Math.round((Number(v) || 0) * 10 ** d) / 10 ** d;
  const baseIdxOf = (units) => { const i = units.findIndex(u => u.isBaseUnit); return i >= 0 ? i : 0; };
  // السعر "تلقائي" إذا كان صفرًا أو يساوي سعر الأساسية × المعامل (لم يعدّله المستخدم يدويًا)
  const isAutoPrice = (price, basePrice, factor) =>
    !toNum(price) || Math.abs(toNum(price) - toNum(basePrice) * toNum(factor)) < 0.01;

  // جعل وحدة أساسية: نقسم كل المعاملات على معاملها فيبقى التعادل صحيحًا
  const makeBase = (units, idx) => {
    const f = toNum(units[idx]?.factor) || 1;
    return units.map((u, i) => ({
      ...u,
      isBaseUnit: i === idx,
      factor: i === idx ? 1 : round(toNum(u.factor) / f, 6),
    }));
  };

  // إزالة وحدة (إن كانت الأساسية: أول وحدة متبقية تصبح أساسية)
  const removeUnit = (idxToRemove) => {
    setFormData((prev) => {
      const wasBase = prev.units[idxToRemove]?.isBaseUnit;
      let units = prev.units.filter((_, idx) => idx !== idxToRemove);
      if (wasBase && units.length) units = makeBase(units, 0);
      return { ...prev, units };
    });
    // أرقام الوحدات تتغير بعد الحذف: نعيد ترقيم رسائل الباركود
    setDup((d) => {
      const next = {};
      Object.entries(d.barcodes).forEach(([k, v]) => {
        const i = Number(k);
        if (i < idxToRemove) next[i] = v; else if (i > idxToRemove) next[i - 1] = v;
      });
      return { ...d, barcodes: next };
    });
  };

  // تعديل الباركود
  const handleBarcodeChange = (uIdx, value) => {
    setFormData((prev) => {
      const units = [...prev.units];
      units[uIdx] = { ...units[uIdx], barcode: value };
      return { ...prev, units };
    });
    setDup((d) => (d.barcodes[uIdx] ? { ...d, barcodes: { ...d.barcodes, [uIdx]: "" } } : d));
  };
  const handleUnitChange = (idx, field, value) => {
    setFormData((prev) => {
      let units = prev.units.map((unit) => ({ ...unit }));
      if (!units[idx]) return prev;
      const bIdx = baseIdxOf(units);
      const base = units[bIdx];

      if (field === "isBaseUnit") {
        if (!value) return prev; // لا بد من وحدة أساسية — اختر غيرها بدل إلغائها
        return { ...prev, units: makeBase(units, idx) };
      }

      if (field === "factor") {
        if (idx === bIdx) return prev; // معامل الأساسية ثابت = 1
        const old = units[idx];
        const autoSal = isAutoPrice(old.salPrice, base.salPrice, old.factor);
        const autoPurch = isAutoPrice(old.purchPrice, base.purchPrice, old.factor);
        units[idx].factor = value; // نص أثناء الكتابة (يسمح بـ 0.5)
        const f = toNum(value);
        if (f > 0) {
          if (autoSal) units[idx].salPrice = round(toNum(base.salPrice) * f, 4);
          if (autoPurch) units[idx].purchPrice = round(toNum(base.purchPrice) * f, 4);
        }
        return { ...prev, units };
      }

      if ((field === "salPrice" || field === "purchPrice") && idx === bIdx) {
        // تغيير سعر الأساسية: نحدّث أسعار الوحدات التي لم يُعدَّل سعرها يدويًا
        const oldBasePrice = base[field];
        units = units.map((u, i) => {
          if (i === bIdx) return { ...u, [field]: value };
          return isAutoPrice(u[field], oldBasePrice, u.factor)
            ? { ...u, [field]: round(toNum(value) * toNum(u.factor), 4) }
            : u;
        });
        return { ...prev, units };
      }

      units[idx][field] = value;
      return { ...prev, units };
    });
  };

  // نص التعادل لوحدة: "1 كرتون = 24 حبة = 4 علبة"
  const unitName = (id) => unitDefs.find(u => u.id === id)?.nameAr || "";
  const equivalenceText = (unit) => {
    const base = formData.units[baseIdxOf(formData.units)];
    if (!base || unit.isBaseUnit) return "";
    const f = toNum(unit.factor);
    if (!(f > 0)) return "أدخل كم " + (unitName(base.unitId) || "وحدة أساسية") + " تحتوي";
    const parts = [`${round(f, 4)} ${unitName(base.unitId)}`];
    formData.units
      .filter(o => !o.isBaseUnit && o !== unit && toNum(o.factor) > 1 && toNum(o.factor) < f)
      .forEach(o => parts.push(`${round(f / toNum(o.factor), 4)} ${unitName(o.unitId)}`));
    return `1 ${unitName(unit.unitId)} = ${parts.join(" = ")}`;
  };

  // الحفظ
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setSuccessMessage('');
    setSaving(true);
    let ok = false;
    try {
      ok = await doSubmit();
    } finally {
      if (!ok) setSaving(false);   // بعد النجاح يبقى الزر معطلًا حتى الانتقال لقائمة الأصناف
    }
  };

  const doSubmit = async () => {

    // ── عدم التكرار: الاسم العربي مطلوب، ثم تحقق نهائي من الخادم لكل الحقول
    if (!formData.arabicName.trim()) {
      setDup((d) => ({ ...d, arabicName: "❌ الاسم العربي مطلوب" }));
      window.scrollTo({ top: 0, behavior: "smooth" });
      setError("❌ أدخل الاسم العربي للصنف.");
      return;
    }
    const localDupIdx = formData.units.findIndex((_, i) => localBarcodeDup(i));
    if (localDupIdx >= 0) {
      setError(`❌ الباركود "${formData.units[localDupIdx].barcode.trim()}" مكرر في أكثر من وحدة لنفس الصنف.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const results = await Promise.all([
      checkUnique("arabicName", formData.arabicName),
      checkUnique("englishName", formData.englishName),
      checkUnique("code", formData.code),
      ...formData.units.map((u, i) => checkUnique("barcode", u.barcode, i)),
    ]);
    const firstConflict = results.find(Boolean);
    if (firstConflict) {
      setError(firstConflict);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const bad = formData.units.find(u => !u.isBaseUnit && !(toNum(u.factor) > 0));
    if (bad) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      setError(`❌ أدخل معامل التحويل للوحدة "${unitName(bad.unitId)}" (كم ${unitName(formData.units[baseIdxOf(formData.units)]?.unitId)} تحتوي).`);
      return;
    }
    const ids = formData.units.map(u => u.unitId);
    if (new Set(ids).size !== ids.length) {
      setError("❌ لا يمكن تكرار نفس الوحدة داخل المنتج.");
      return;
    }

    const payload = {
      ...formData,
      units: formData.units.map(u => ({
        ...u,
        factor: u.isBaseUnit ? 1 : toNum(u.factor),
        salPrice: toNum(u.salPrice),
        purchPrice: toNum(u.purchPrice),
      })),
    };

    try {
      if (isEdit) await axios.put(`${API_BASE_URL}/Product/${companyId}/${formData.itemId}`, payload);
      else await axios.post(`${API_BASE_URL}/Product`, payload);
      setSuccessMessage(isEdit ? "✅ تم تعديل المنتج بنجاح!" : "✅ تم إضافة المنتج بنجاح!");
      setTimeout(() => {
        navigate("/productList");
      }, 1500);
      return true;
    } catch (err) {
      console.error("تفاصيل الخطأ:", err.response?.data || err);
      const errorMessage =
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.response?.data?.message ||
        err.response?.data?.innerError ||
        err.message;
      setError(String(errorMessage).startsWith("❌") ? errorMessage : "❌ حدث خطأ أثناء الحفظ: " + errorMessage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" flexDirection="column" gap={2}>
        <CircularProgress size={isMobile ? 40 : 60} />
        <Typography variant={isMobile ? "body1" : "h6"}>جاري التحميل...</Typography>
      </Box>
    );
  }

  return (
    <Container
      maxWidth={false}
      sx={{
        mt: { xs: 2, sm: 3, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 3, sm: 4, md: 5 }
      }}
      dir="rtl"
    >
      
{/* Header - يعتمد على الثيم */}
<Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
    px: { xs: 1, sm: 2 },
    py: 1.5,
    mb: 2,
    borderBottom: 1,
    borderColor: 'divider',
  }}
>
  <InventoryIcon
    sx={{
      fontSize: 26,
      color: 'primary.main',
    }}
  />

  <Typography
    variant="h6"
    sx={{
      fontWeight: 800,
      color: 'text.primary',
      fontSize: { xs: '1rem', sm: '1.2rem' },
    }}
  >
    {isEdit ? "تعديل المنتج" : "إضافة منتج جديد"}
  </Typography>
</Box>


      {/* رسائل النجاح والخطأ */}
      {successMessage && (
        <Fade in={!!successMessage}>
          <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        </Fade>
      )}
      {error && (
        <Fade in={!!error}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Fade>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {/* معلومات المنتج الأساسية */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 3, sm: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
              pb: 1.5,
              borderBottom: '2px solid',
              borderColor: 'primary.main',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            <InventoryIcon color="primary" />
            معلومات المنتج الأساسية
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="الاسم العربي"
                name="arabicName"
                value={formData.arabicName}
                onChange={handleChange}
                onBlur={(e) => checkUnique("arabicName", e.target.value)}
                error={!!dup.arabicName}
                helperText={dup.arabicName || (checking.arabicName ? "جارٍ التحقق..." : " ")}
                onWheel={preventWheelChange}
                 onFocus={handleFocus}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalOfferIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="الاسم الإنجليزي"
                name="englishName"
                value={formData.englishName}
                onChange={handleChange}
                onBlur={(e) => checkUnique("englishName", e.target.value)}
                error={!!dup.englishName}
                helperText={dup.englishName || (checking.englishName ? "جارٍ التحقق..." : " ")}
                onWheel={preventWheelChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalOfferIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="رقم المنتج (ItemId)"
                name="itemId"
                value={formData.itemId || ""}
                disabled
                helperText={isEdit ? " " : "يُولَّد تلقائيًا عند الحفظ"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NumbersIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="كود المنتج"
                name="code"
                value={formData.code}
                onChange={handleChange}
                onBlur={(e) => checkUnique("code", e.target.value)}
                error={!!dup.code}
                helperText={dup.code || (checking.code ? "جارٍ التحقق..." : " ")}
                onFocus={handleFocus}
                onWheel={preventWheelChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>المجموعة</InputLabel>
                <Select
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={handleChange}
                  label="المجموعة"
                  sx={{ borderRadius: 2 }}
                >
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CategoryIcon fontSize="small" />
                        {c.nameAr}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>نوع الضريبة</InputLabel>
                <Select
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  label="نوع الضريبة"
                  sx={{ borderRadius: 2 }}
                >
                  {vatTypes.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="الوصف"
                name="description"
                value={formData.description}
                onChange={handleChange}
                onWheel={preventWheelChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="isService"
                    checked={formData.isService}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label="خدمة (ليس منتج)"
              />
              {formData.isService && (
                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: -0.5 }}>
                  الخدمة ليس لها مخزون: لا رصيد ولا حركات مخزنية ولا تكلفة، ولا تظهر في الإدخال والإخراج والمناقلة والجرد.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    name="showInPOS"
                    checked={formData.showInPOS}
                    onChange={handleChange}
                    color="success"
                  />
                }
                label="إظهار في نقطة البيع"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* الوحدات والباركود */}
        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 3, sm: 4 },
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <Typography
              variant="h6"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: { xs: '1.1rem', sm: '1.25rem' }
              }}
            >
              <ScaleIcon color="secondary" />
              الوحدات والباركود ({formData.units.length})
            </Typography>
               <Button
  variant="contained"
  color="primary"
  size="small"
  startIcon={<AddIcon />}
  onClick={() => navigate('/add-unit')}
>
  تعريف وحدة جديدة
</Button>
           <Button
  variant="contained"
  color="info"
  size="small"
  startIcon={<AddIcon />}
  onClick={addUnit}
>
  إضافة وحدة
</Button>
          </Box>

          {formData.units.length > 1 && (
            <Alert severity="info" icon={<ScaleIcon />} sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
              <Typography sx={{ fontWeight: 800, mb: 0.5 }}>تعادل الوحدات</Typography>
              {[...formData.units]
                .sort((a, b) => (a.isBaseUnit ? 0 : toNum(a.factor)) - (b.isBaseUnit ? 0 : toNum(b.factor)))
                .map((u) => (
                  <Typography key={u.unitId} sx={{ fontSize: 14 }}>
                    {u.isBaseUnit ? `${unitName(u.unitId)} — الوحدة الأساسية (1)` : equivalenceText(u)}
                  </Typography>
                ))}
            </Alert>
          )}

          {formData.units.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
              <ScaleIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography color="text.secondary">
                لا توجد وحدات. قم بإضافة وحدة أولى للمنتج
              </Typography>
            </Paper>
          ) : (
            formData.units.map((unit, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{
  mb: 2,
  p: { xs: 2, sm: 3 },
  borderRadius: 2,
  border: '1px solid',
  borderColor: unit.isBaseUnit ? 'primary.main' : 'divider',
  boxShadow: 'none',
  transition: 'border-color 0.2s ease',
}}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                    flexWrap: 'wrap',
                    gap: 1
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      الوحدة #{idx + 1}
                    </Typography>
                    {unit.isBaseUnit && (
                      <Chip
                        label="وحدة أساسية"
                        color="primary"
                        size="small"
                        icon={<ScaleIcon />}
                      />
                    )}
                  </Box>
                  <Tooltip title="حذف الوحدة">
                    <IconButton
                      color="error"
                      onClick={() => removeUnit(idx)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'error.main',
                        '&:hover': { bgcolor: 'error.light' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Grid container spacing={{ xs: 2, sm: 2.5 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>الوحدة</InputLabel>
                      <Select
                        value={unit.unitId}
                        onChange={(e) => handleUnitChange(idx, "unitId", e.target.value)}
                        label="الوحدة"
                        sx={{ borderRadius: 2 }}
                      >
                        {unitDefs.map((u) => (
                          <MenuItem key={u.id} value={u.id}
                            disabled={formData.units.some((o, j) => j !== idx && o.unitId === u.id)}>
                            {u.nameAr}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6} sm={6} md={4}>
                    <TextField
                        fullWidth
                        label="سعر الشراء"
                        type="number"
                        value={unit.purchPrice}
                        onFocus={handleFocus}
                        onChange={(e) => handleUnitChange(idx, "purchPrice", e.target.value)}
                       onWheel={preventWheelChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon color="success" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label="سعر البيع"
                        type="number"
                        value={unit.salPrice}
                        onFocus={handleFocus}
                        onChange={(e) => handleUnitChange(idx, "salPrice", e.target.value)}
                        onWheel={preventWheelChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <AttachMoneyIcon color="error" />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={6} md={4}>
                      <TextField
                        fullWidth
                        label={unit.isBaseUnit ? "معامل التحويل (أساسية)" : `يحتوي كم ${unitName(formData.units[baseIdxOf(formData.units)]?.unitId) || "وحدة أساسية"}`}
                        type="number"
                        value={unit.isBaseUnit ? 1 : unit.factor}
                        disabled={unit.isBaseUnit}
                        required={!unit.isBaseUnit}
                        error={!unit.isBaseUnit && unit.factor !== "" && !(toNum(unit.factor) > 0)}
                        onFocus={handleFocus}
                        onChange={(e) => handleUnitChange(idx, "factor", e.target.value)}
                        onWheel={preventWheelChange}
                        inputProps={{ min: 0, step: "any" }}
                        helperText={unit.isBaseUnit ? "كل الأرصدة تُحفظ بهذه الوحدة" : (equivalenceText(unit) || " ")}
                        FormHelperTextProps={{ sx: { fontWeight: 700, color: unit.isBaseUnit ? 'text.secondary' : 'primary.main', mx: 0.5 } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="الباركود"
                      value={unit.barcode || ""}
                      onChange={(e) => handleBarcodeChange(idx, e.target.value)}
                      onBlur={(e) => checkUnique("barcode", e.target.value, idx)}
                      error={!!barcodeError(idx)}
                      helperText={barcodeError(idx) || (checking[`b${idx}`] ? "جارٍ التحقق..." : " ")}
                     onWheel={preventWheelChange}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <QrCodeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={unit.isBaseUnit}
                            onChange={(e) => handleUnitChange(idx, "isBaseUnit", e.target.checked)}
                            color="primary"
                          />
                        }
                        label="وحدة أساسية"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            ))
          )}
        </Paper>

        {/* أزرار الحفظ والإلغاء */}
        <Paper
  elevation={0}
  sx={{
    p: { xs: 2, sm: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
  }}
>
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      gap: 1,
      flexWrap: 'wrap',
    }}
  >
    <Button
      variant="outlined"
      color="secondary"
      size="medium"
      onClick={() => navigate('/productList')}
      sx={{ minWidth: { xs: '100%', sm: 100 } }}
    >
      إلغاء
    </Button>

    <Button
      type="submit"
      variant="contained"
      color="primary"
      size="medium"
      disabled={saving}
      startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
      sx={{ minWidth: { xs: '100%', sm: 140 } }}
    >
      حفظ 
    </Button>
  </Box>
</Paper>
      </Box>
    </Container>
  );
}